import { ZonedDateTimeFormatter } from '@csp/csp-common-date-util';
import { DeviceObservationRequestRef } from '@csp/csp-common-devices';
import { DeviceScheduleRequestStatusType, DeviceSessionStatus } from '@csp/csp-common-metrics';
import { ApiOptions, ZonedDateTime } from '@csp/csp-common-model';
import { toRestOptions } from '@csp/csp-fe-auth';
import { DeviceSessionsRestServiceV1 } from '@csp/dmdp-api-client';
import { largePage } from '@csp/dmdp-api-common-dto';
import {
  DeviceSessionCriteriaFieldV1,
  DeviceSessionQueryCriterionV1,
  DeviceSessionQueryOperatorV1,
  DeviceSessionQueryTypeV1,
  DeviceSessionV1,
} from '@csp/dmdp-api-device-dto';
import { getDateRange } from '../util/DateRange';

export type RunInSessionsInput = {
  randomizedTimestamp: ZonedDateTime;
  runInDays: number;
  deviceModelCodes: string[];
  scheduleRequestRef: DeviceObservationRequestRef;
  userId: string;
  apiOptions?: ApiOptions;
};

export const createDeviceSessionDateRangeCriteria = (
  lastDayTimestamp: ZonedDateTime,
  nrOfDays: number,
): DeviceSessionQueryCriterionV1[] => {
  const { fromDate, toDate } = getDateRange({ lastDayTimestamp, nrOfDays });

  return [
    {
      fieldCriterion: {
        field: DeviceSessionCriteriaFieldV1.SESSION_TIMESTAMP,
        type: DeviceSessionQueryTypeV1.GREATER_THAN_EQ,
        values: [ZonedDateTimeFormatter.toZonedDateTimeString(fromDate)],
      },
    },
    {
      fieldCriterion: {
        field: DeviceSessionCriteriaFieldV1.SESSION_TIMESTAMP,
        type: DeviceSessionQueryTypeV1.LESS_THAN_EQ,
        values: [ZonedDateTimeFormatter.toZonedDateTimeString(toDate)],
      },
    },
  ];
};

export const createDeviceSessionRequestRefCriteria = (
  scheduleRequestRef: DeviceObservationRequestRef,
): DeviceSessionQueryCriterionV1[] => [
  {
    fieldCriterion: {
      field: DeviceSessionCriteriaFieldV1.SCHEDULE_REQUEST_REF_SCHEDULE_CODE,
      type: DeviceSessionQueryTypeV1.EQ,
      values: [scheduleRequestRef.scheduleCode],
    },
  },
  {
    fieldCriterion: {
      field: DeviceSessionCriteriaFieldV1.SCHEDULE_REQUEST_REF_REQUEST_CODE,
      type: DeviceSessionQueryTypeV1.EQ,
      values: [scheduleRequestRef.requestCode],
    },
  },
  {
    fieldCriterion: {
      field: DeviceSessionCriteriaFieldV1.SCHEDULE_REQUEST_REF_VERSION_CODE,
      type: DeviceSessionQueryTypeV1.EQ,
      values: [scheduleRequestRef.versionCode],
    },
  },
];

export const createDeviceSessionScheduledCriterion = (): DeviceSessionQueryCriterionV1 => ({
  fieldCriterion: {
    field: DeviceSessionCriteriaFieldV1.SCHEDULE_REQUEST_STATUS,
    type: DeviceSessionQueryTypeV1.EQ,
    values: [DeviceScheduleRequestStatusType.SCHEDULED],
  },
});

export const createDeviceSessionIncompleteCriterion = (): DeviceSessionQueryCriterionV1 => ({
  fieldCriterion: {
    field: DeviceSessionCriteriaFieldV1.SESSION_STATUS,
    type: DeviceSessionQueryTypeV1.EQ,
    values: [DeviceSessionStatus.INCOMPLETE],
  },
});

export const createDeviceSessionUserIdCriterion = (userId: string): DeviceSessionQueryCriterionV1 => ({
  fieldCriterion: {
    field: DeviceSessionCriteriaFieldV1.USER_ID,
    type: DeviceSessionQueryTypeV1.EQ,
    values: [userId],
  },
});

export const createDeviceSessionDeviceModelCriterion = (deviceModelCodes: string[]): DeviceSessionQueryCriterionV1 => ({
  fieldCriterion: {
    field: DeviceSessionCriteriaFieldV1.META_VALUE,
    subField: 'DEVICE_MODEL_NUMBER',
    type: DeviceSessionQueryTypeV1.IN,
    values: deviceModelCodes,
  },
});

export const createDeviceSessionInvalidatedCriterion = (invalidated: boolean): DeviceSessionQueryCriterionV1 => ({
  fieldCriterion: {
    field: DeviceSessionCriteriaFieldV1.INVALIDATED,
    type: DeviceSessionQueryTypeV1.EXISTS,
    values: [invalidated],
  },
});

export const queryIncompleteRunInDeviceSessions = async ({
  randomizedTimestamp,
  runInDays,
  deviceModelCodes,
  scheduleRequestRef,
  userId,
  apiOptions,
}: RunInSessionsInput): Promise<DeviceSessionV1[] | undefined> => {
  const { sessions } = await DeviceSessionsRestServiceV1.queryDeviceSessions(
    toRestOptions(apiOptions),
    {
      criterion: {
        operator: DeviceSessionQueryOperatorV1.AND,
        criteria: [
          createDeviceSessionScheduledCriterion(),
          createDeviceSessionIncompleteCriterion(),
          createDeviceSessionUserIdCriterion(userId),
          createDeviceSessionDeviceModelCriterion(deviceModelCodes),
          createDeviceSessionInvalidatedCriterion(false),
          ...createDeviceSessionRequestRefCriteria(scheduleRequestRef),
          ...createDeviceSessionDateRangeCriteria(randomizedTimestamp, runInDays),
        ],
      },
    },
    largePage(),
  );

  return sessions;
};
